<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import AiAgentsAPI from '../../../../../api/aiAgents';

const props = defineProps({
  agent: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();
const alert = useAlert;

const products = ref([]);
const isLoading = ref(false);
const showForm = ref(false);
const editingId = ref(null);
const searchQuery = ref('');
const catalogSchema = ref({ catalog_type: 'products', fields: [] });
const isSavingType = ref(false);

const catalogType = computed(
  () => catalogSchema.value.catalog_type || 'products'
);
const catalogFields = computed(() => catalogSchema.value.fields || []);

const defaultMetadata = computed(() => {
  const metadata = {};
  catalogFields.value.forEach(field => {
    metadata[field.key] = '';
  });
  return metadata;
});

const defaultVariant = {
  name: '',
  price: '',
  sku: '',
  availability: 'in_stock',
};

const baseProduct = computed(() => ({
  name: '',
  sku: '',
  short_description: '',
  product_url: '',
  price: '',
  currency: 'USD',
  category: '',
  keywords: '',
  availability: 'in_stock',
  promotion_status: 'none',
  priority: 0,
  status: 'active',
  faq: {},
  related_products: [],
  metadata: defaultMetadata.value,
  variants: [],
}));

const product = ref({});
const selectedMedia = ref(null);
const mediaPreview = ref('');

const resetProduct = () => {
  product.value = JSON.parse(JSON.stringify(baseProduct.value));
};

const loadCatalogSchema = async () => {
  try {
    const { data } = await AiAgentsAPI.getCatalogSchema(props.agent.id);
    catalogSchema.value = data;
    resetProduct();
  } catch (error) {
    catalogSchema.value = { catalog_type: 'products', fields: [] };
    resetProduct();
  }
};

const loadProducts = async () => {
  isLoading.value = true;
  try {
    const { data } = await AiAgentsAPI.getProducts(props.agent.id);
    products.value = data;
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  loadCatalogSchema();
  loadProducts();
});

const filteredProducts = computed(() => {
  if (!searchQuery.value.trim()) return products.value;
  const q = searchQuery.value.toLowerCase();
  return products.value.filter(
    p =>
      p.name?.toLowerCase().includes(q) ||
      p.sku?.toLowerCase().includes(q) ||
      p.category?.toLowerCase().includes(q) ||
      p.keywords?.some(k => k.toLowerCase().includes(q))
  );
});

const openForm = () => {
  resetProduct();
  editingId.value = null;
  selectedMedia.value = null;
  mediaPreview.value = '';
  showForm.value = true;
};

const openEdit = p => {
  product.value = {
    ...baseProduct.value,
    ...p,
    keywords: Array.isArray(p.keywords)
      ? p.keywords.join(', ')
      : p.keywords || '',
    metadata: { ...defaultMetadata.value, ...(p.metadata || {}) },
    variants: Array.isArray(p.variants)
      ? p.variants.map(v => ({ ...defaultVariant, ...v }))
      : [],
  };
  editingId.value = p.id;
  selectedMedia.value = null;
  mediaPreview.value = p.media_url || '';
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
  editingId.value = null;
};

const onMediaChange = e => {
  const file = e.target.files?.[0];
  if (!file) return;
  selectedMedia.value = file;
  mediaPreview.value = URL.createObjectURL(file);
};

const save = async () => {
  const payload = new FormData();
  Object.entries(product.value).forEach(([key, value]) => {
    if (key === 'keywords') {
      payload.append(
        key,
        value
          .split(',')
          .map(k => k.trim())
          .filter(Boolean)
      );
    } else if (key === 'metadata' || key === 'faq' || key === 'variants') {
      payload.append(key, JSON.stringify(value));
    } else if (value !== null && value !== undefined) {
      payload.append(key, value);
    }
  });
  if (selectedMedia.value) payload.append('media', selectedMedia.value);

  if (editingId.value) {
    await AiAgentsAPI.updateProduct(props.agent.id, editingId.value, payload);
  } else {
    await AiAgentsAPI.createProduct(props.agent.id, payload);
  }
  closeForm();
  await loadProducts();
};

const removeProduct = async id => {
  await AiAgentsAPI.deleteProduct(props.agent.id, id);
  await loadProducts();
};

const availabilityOptions = [
  { value: 'in_stock', label: t('AI_AGENTS.CATALOG.AVAILABILITY_IN_STOCK') },
  { value: 'low_stock', label: t('AI_AGENTS.CATALOG.AVAILABILITY_LOW_STOCK') },
  {
    value: 'out_of_stock',
    label: t('AI_AGENTS.CATALOG.AVAILABILITY_OUT_OF_STOCK'),
  },
  { value: 'pre_order', label: t('AI_AGENTS.CATALOG.AVAILABILITY_PRE_ORDER') },
];

const promotionOptions = [
  { value: 'none', label: t('AI_AGENTS.CATALOG.PROMOTION_NONE') },
  { value: 'active', label: t('AI_AGENTS.CATALOG.PROMOTION_ACTIVE') },
  { value: 'expired', label: t('AI_AGENTS.CATALOG.PROMOTION_EXPIRED') },
];

const addVariant = () => {
  if (!product.value.variants) product.value.variants = [];
  product.value.variants.push({ ...defaultVariant });
};

const removeVariant = index => {
  product.value.variants.splice(index, 1);
};

const fieldLabel = key => {
  return t(`AI_AGENTS.CATALOG.FIELDS.${key.toUpperCase()}`, key);
};

const fieldInputType = field => {
  return field.type === 'number' ? 'number' : 'text';
};

const productCardSubtitle = p => {
  if (catalogType.value === 'vehicles') {
    return [p.metadata?.brand, p.metadata?.model, p.metadata?.year]
      .filter(Boolean)
      .join(' ');
  }
  if (catalogType.value === 'real_estate') {
    return [p.metadata?.bedrooms, p.metadata?.bathrooms, p.metadata?.area]
      .filter(Boolean)
      .map((v, i) => {
        if (i === 0) return `${v} dorm.`;
        if (i === 1) return `${v} baños`;
        return `${v} m²`;
      })
      .join(' · ');
  }
  if (catalogType.value === 'services') {
    return p.metadata?.duration || p.short_description;
  }
  return p.short_description;
};

const shouldShowVariants = computed(() => catalogType.value === 'products');

const catalogTypeOptions = [
  {
    value: 'products',
    label: t('AI_AGENTS.CATALOG.TYPES.PRODUCTS'),
    description: t('AI_AGENTS.CATALOG.CATALOG_TYPE_DESCRIPTIONS.PRODUCTS'),
    icon: 'i-lucide-package',
  },
  {
    value: 'services',
    label: t('AI_AGENTS.CATALOG.TYPES.SERVICES'),
    description: t('AI_AGENTS.CATALOG.CATALOG_TYPE_DESCRIPTIONS.SERVICES'),
    icon: 'i-lucide-wrench',
  },
  {
    value: 'vehicles',
    label: t('AI_AGENTS.CATALOG.TYPES.VEHICLES'),
    description: t('AI_AGENTS.CATALOG.CATALOG_TYPE_DESCRIPTIONS.VEHICLES'),
    icon: 'i-lucide-car',
  },
  {
    value: 'real_estate',
    label: t('AI_AGENTS.CATALOG.TYPES.REAL_ESTATE'),
    description: t('AI_AGENTS.CATALOG.CATALOG_TYPE_DESCRIPTIONS.REAL_ESTATE'),
    icon: 'i-lucide-home',
  },
];

const changeCatalogType = async newType => {
  if (newType === catalogType.value || isSavingType.value) return;
  isSavingType.value = true;
  try {
    await store.dispatch('aiAgents/update', {
      id: props.agent.id,
      data: { catalog_type: newType },
    });
    alert(t('AI_AGENTS.MESSAGES.UPDATE_SUCCESS'));
    await loadCatalogSchema();
    await loadProducts();
  } catch (error) {
    alert(t('AI_AGENTS.MESSAGES.UPDATE_ERROR'));
  } finally {
    isSavingType.value = false;
  }
};

watch(
  () => props.agent.catalog_type,
  (newType, oldType) => {
    if (newType && newType !== oldType) {
      loadCatalogSchema();
      loadProducts();
    }
  }
);
</script>

<template>
  <div class="flex flex-col gap-4">
    <div
      v-if="products.length"
      class="flex items-center gap-3 p-4 rounded-xl border border-n-slate-6 bg-n-surface-2"
    >
      <span
        :class="catalogTypeOptions.find(o => o.value === catalogType)?.icon"
        class="w-8 h-8 text-n-brand shrink-0"
      />
      <div>
        <div class="text-sm font-semibold text-n-slate-12">
          {{ catalogTypeOptions.find(o => o.value === catalogType)?.label }}
        </div>
        <div class="text-xs text-n-slate-10">
          {{
            catalogTypeOptions.find(o => o.value === catalogType)?.description
          }}
        </div>
      </div>
      <div class="ml-auto text-xs text-n-slate-10 text-right">
        {{ t('AI_AGENTS.CATALOG.CATALOG_TYPE_LOCKED') }}
      </div>
    </div>

    <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      <button
        v-for="opt in catalogTypeOptions"
        :key="opt.value"
        class="flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all text-center"
        :class="
          catalogType === opt.value
            ? 'border-n-brand bg-n-brand/5 text-n-brand'
            : 'border-n-slate-6 hover:border-n-slate-8 text-n-slate-11'
        "
        :disabled="isSavingType"
        @click="changeCatalogType(opt.value)"
      >
        <span :class="opt.icon" class="w-8 h-8" />
        <span class="text-sm font-semibold">{{ opt.label }}</span>
        <span class="text-xs leading-relaxed opacity-80">{{
          opt.description
        }}</span>
      </button>
    </div>

    <div class="flex items-center justify-between gap-4">
      <input
        v-model="searchQuery"
        type="text"
        class="flex-1 px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
        :placeholder="t('AI_AGENTS.CATALOG.SEARCH_PLACEHOLDER')"
      />
      <button
        class="px-4 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:bg-n-brand/90"
        @click="openForm"
      >
        {{ t('AI_AGENTS.CATALOG.ADD') }}
      </button>
    </div>

    <div v-if="isLoading" class="text-sm text-n-slate-10">
      {{ t('AI_AGENTS.LOADING') }}
    </div>

    <div v-else-if="!products.length" class="text-sm text-n-slate-10">
      {{ t('AI_AGENTS.CATALOG.NO_RESULTS') }}
    </div>

    <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <div
        v-for="p in filteredProducts"
        :key="p.id"
        class="rounded-xl border border-n-slate-6 bg-n-surface-1 p-4 flex flex-col gap-2"
      >
        <div class="flex items-center gap-3">
          <img
            v-if="p.media_url"
            :src="p.media_url"
            class="w-16 h-16 rounded-lg object-cover bg-n-slate-6"
          />
          <div class="flex-1 min-w-0">
            <div class="text-sm font-semibold text-n-slate-12 truncate">
              {{ p.name }}
            </div>
            <div class="text-xs text-n-slate-10">{{ p.sku }}</div>
          </div>
        </div>
        <div class="text-xs text-n-slate-10 line-clamp-2">
          {{ productCardSubtitle(p) }}
        </div>
        <div class="flex items-center gap-2 text-xs">
          <span class="px-2 py-0.5 rounded-full bg-n-slate-7 text-n-slate-11">{{
            p.category
          }}</span>
          <span class="px-2 py-0.5 rounded-full bg-n-brand/10 text-n-brand">
            {{ p.currency }} {{ p.price }}
          </span>
        </div>
        <div
          class="flex items-center gap-2 mt-auto pt-2 border-t border-n-slate-6"
        >
          <button
            class="text-xs text-n-brand hover:text-n-brand/80"
            @click="openEdit(p)"
          >
            {{ t('AI_AGENTS.TABLE.EDIT') }}
          </button>
          <button
            class="text-xs text-n-ruby-9 hover:text-n-ruby-10"
            @click="removeProduct(p.id)"
          >
            {{ t('AI_AGENTS.KNOWLEDGE.DELETE') }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="showForm"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto p-4"
      @click.self="closeForm"
    >
      <div
        class="w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6 rounded-xl bg-n-surface-1 shadow-xl my-auto"
      >
        <h3 class="mb-4 text-lg font-semibold">
          {{
            editingId ? t('AI_AGENTS.CATALOG.EDIT') : t('AI_AGENTS.CATALOG.ADD')
          }}
        </h3>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label class="grid gap-1">
            <span class="text-sm font-medium">{{
              t('AI_AGENTS.CATALOG.NAME')
            }}</span>
            <input
              v-model="product.name"
              type="text"
              class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
            />
          </label>
          <label class="grid gap-1">
            <span class="text-sm font-medium">{{
              t('AI_AGENTS.CATALOG.SKU')
            }}</span>
            <input
              v-model="product.sku"
              type="text"
              class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
            />
          </label>
          <label class="grid gap-1 sm:col-span-2">
            <span class="text-sm font-medium">{{
              t('AI_AGENTS.CATALOG.SHORT_DESCRIPTION')
            }}</span>
            <input
              v-model="product.short_description"
              type="text"
              class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
            />
          </label>
          <label class="grid gap-1">
            <span class="text-sm font-medium">{{
              t('AI_AGENTS.CATALOG.PRODUCT_URL')
            }}</span>
            <input
              v-model="product.product_url"
              type="url"
              class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
            />
          </label>
          <div class="grid grid-cols-2 gap-4">
            <label class="grid gap-1">
              <span class="text-sm font-medium">{{
                t('AI_AGENTS.CATALOG.PRICE')
              }}</span>
              <input
                v-model.number="product.price"
                type="number"
                step="0.01"
                class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
              />
            </label>
            <label class="grid gap-1">
              <span class="text-sm font-medium">{{
                t('AI_AGENTS.CATALOG.CURRENCY')
              }}</span>
              <input
                v-model="product.currency"
                type="text"
                class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
              />
            </label>
          </div>
          <label class="grid gap-1">
            <span class="text-sm font-medium">{{
              t('AI_AGENTS.CATALOG.CATEGORY')
            }}</span>
            <input
              v-model="product.category"
              type="text"
              class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
            />
          </label>
          <label class="grid gap-1">
            <span class="text-sm font-medium">{{
              t('AI_AGENTS.CATALOG.KEYWORDS')
            }}</span>
            <input
              v-model="product.keywords"
              type="text"
              class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
              :placeholder="t('AI_AGENTS.CATALOG.KEYWORDS_PLACEHOLDER')"
            />
          </label>
          <label class="grid gap-1">
            <span class="text-sm font-medium">{{
              t('AI_AGENTS.CATALOG.AVAILABILITY')
            }}</span>
            <select
              v-model="product.availability"
              class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
            >
              <option
                v-for="opt in availabilityOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </label>
          <label class="grid gap-1">
            <span class="text-sm font-medium">{{
              t('AI_AGENTS.CATALOG.PROMOTION')
            }}</span>
            <select
              v-model="product.promotion_status"
              class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
            >
              <option
                v-for="opt in promotionOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </label>
          <label class="grid gap-1">
            <span class="text-sm font-medium">{{
              t('AI_AGENTS.CATALOG.PRIORITY')
            }}</span>
            <input
              v-model.number="product.priority"
              type="number"
              min="0"
              max="10"
              class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
            />
          </label>
          <label class="grid gap-1">
            <span class="text-sm font-medium">{{
              t('AI_AGENTS.CATALOG.STATUS')
            }}</span>
            <select
              v-model="product.status"
              class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
            >
              <option value="active">
                {{ t('AI_AGENTS.STATUSES.ACTIVE') }}
              </option>
              <option value="inactive">
                {{ t('AI_AGENTS.STATUSES.INACTIVE') }}
              </option>
            </select>
          </label>

          <label
            v-for="field in catalogFields"
            :key="field.key"
            class="grid gap-1"
          >
            <span class="text-sm font-medium">{{ fieldLabel(field.key) }}</span>
            <input
              v-model="product.metadata[field.key]"
              :type="fieldInputType(field)"
              class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
            />
          </label>

          <label class="grid gap-1 sm:col-span-2">
            <span class="text-sm font-medium">{{
              t('AI_AGENTS.CATALOG.MEDIA')
            }}</span>
            <input
              type="file"
              accept="image/*,video/*"
              class="text-sm"
              @change="onMediaChange"
            />
            <img
              v-if="mediaPreview"
              :src="mediaPreview"
              class="w-24 h-24 rounded-lg object-cover mt-2"
            />
          </label>

          <div v-if="shouldShowVariants" class="sm:col-span-2 grid gap-3">
            <div class="flex items-center justify-between">
              <span class="text-sm font-medium">{{
                t('AI_AGENTS.CATALOG.VARIANTS_TITLE')
              }}</span>
              <button
                class="px-3 py-1 text-xs font-medium rounded-lg bg-n-slate-7 text-n-slate-12 hover:bg-n-slate-6"
                @click="addVariant"
              >
                <span class="i-lucide-plus w-3 h-3 inline-block" />
                {{ t('AI_AGENTS.CATALOG.ADD_VARIANT') }}
              </button>
            </div>
            <div
              v-for="(variant, vIndex) in product.variants"
              :key="vIndex"
              class="grid grid-cols-1 sm:grid-cols-4 gap-2 p-3 rounded-lg border border-n-slate-6 bg-n-surface-2"
            >
              <input
                v-model="variant.name"
                type="text"
                :placeholder="t('AI_AGENTS.CATALOG.VARIANT_NAME_PLACEHOLDER')"
                class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-1"
              />
              <input
                v-model="variant.price"
                type="text"
                :placeholder="t('AI_AGENTS.CATALOG.VARIANT_PRICE_PLACEHOLDER')"
                class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-1"
              />
              <input
                v-model="variant.sku"
                type="text"
                :placeholder="t('AI_AGENTS.CATALOG.VARIANT_SKU_PLACEHOLDER')"
                class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-1"
              />
              <div class="flex items-center gap-2">
                <select
                  v-model="variant.availability"
                  class="flex-1 px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-1"
                >
                  <option
                    v-for="opt in availabilityOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
                <button
                  class="p-1 text-n-ruby-9 hover:text-n-ruby-10"
                  @click="removeVariant(vIndex)"
                >
                  <span class="i-lucide-trash-2 w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="flex justify-end gap-2 mt-6">
          <button
            class="px-4 py-2 text-sm font-medium rounded-lg border border-n-slate-6"
            @click="closeForm"
          >
            {{ t('AI_AGENTS.CANCEL') }}
          </button>
          <button
            class="px-4 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:bg-n-brand/90"
            @click="save"
          >
            {{ t('AI_AGENTS.FORM.SAVE') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
